import { apiClient } from './apiClient';

const API_BASE_URL = '/api/v1';

export const productService = {
  getAllProducts: async () => {
    try {
      const response = await apiClient.get(`${API_BASE_URL}/products?limit=1000`);
      const products = response.data.data || response.data || [];
      return Array.isArray(products) ? products : [];
    } catch (error) {
      console.error('Failed to fetch all products:', error);
      return [];
    }
  },

  /**
   * Get paginated products for catalog with filters
   */
  getCatalogProducts: async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      
      // Add pagination
      params.append('page', String(filters.page || 1));
      params.append('limit', String(filters.limit || 12));

      // Add search term
      if (filters.search && filters.search.trim()) {
        params.append('search', filters.search.trim());
      }

      // Add filter values
      const filterKeys = ['category', 'productName', 'finish', 'color', 'suitableFor', 'material', 'collection', 'packType'];
      filterKeys.forEach((key) => {
        if (filters[key] && Array.isArray(filters[key]) && filters[key].length > 0) {
          // Join IDs as comma-separated string
          params.append(key, filters[key].map(String).join(','));
        }
      });

      const queryString = params.toString();
      const url = queryString ? `${API_BASE_URL}/products?${queryString}` : `${API_BASE_URL}/products`;
      
      const response = await apiClient.get(url);
      
      // Ensure we return the correct structure
      return {
        data: response.data?.data || [],
        has_more: response.data?.has_more || false,
        current_page: response.data?.current_page,
        last_page: response.data?.last_page,
        total: response.data?.total,
      };
    } catch (error) {
      console.error('Failed to fetch catalog products:', error);
      return { data: [], has_more: false };
    }
  },

  /**
   * Extract filter options from all products
   */
  getFilterOptions: async () => {
    try {
      const allProducts = await productService.getAllProducts();
      
      const options = {
        category: [],
        productName: [],
        finish: [],
        color: [],
        suitableFor: [],
        material: [],
        collection: [],
        packType: [],
      };

      // Extract unique values from API response (uses camelCase singular field names)
      allProducts.forEach((product) => {
        // Category ID
        if (product.categoryId && !options.category.find(o => o.id === product.categoryId)) {
          options.category.push({
            id: product.categoryId,
            name: product.category || `Category ${product.categoryId}`
          });
        }
        
        // Product Name ID
        if (product.productNameId && !options.productName.find(o => o.id === product.productNameId)) {
          options.productName.push({
            id: product.productNameId,
            name: product.productName || `Product ${product.productNameId}`
          });
        }
        
        // Finish (many-to-many) - API returns as 'finish' (singular)
        if (product.finish && Array.isArray(product.finish)) {
          product.finish.forEach((item) => {
            if (!options.finish.find(o => o.id === item.id)) {
              options.finish.push({ id: item.id, name: item.name });
            }
          });
        }
        
        // Color (many-to-many) - API returns as 'color' (singular)
        if (product.color && Array.isArray(product.color)) {
          product.color.forEach((item) => {
            if (!options.color.find(o => o.id === item.id)) {
              options.color.push({ id: item.id, name: item.name });
            }
          });
        }
        
        // Suitable For ID
        if (product.suitableForId && !options.suitableFor.find(o => o.id === product.suitableForId)) {
          options.suitableFor.push({
            id: product.suitableForId,
            name: product.suitableFor || `Suitable For ${product.suitableForId}`
          });
        }
        
        // Material (many-to-many) - API returns as 'material' (singular)
        if (product.material && Array.isArray(product.material)) {
          product.material.forEach((item) => {
            if (!options.material.find(o => o.id === item.id)) {
              options.material.push({ id: item.id, name: item.name });
            }
          });
        }
        
        // Collection (many-to-many) - API returns as 'collection' (singular)
        if (product.collection && Array.isArray(product.collection)) {
          product.collection.forEach((item) => {
            if (!options.collection.find(o => o.id === item.id)) {
              options.collection.push({ id: item.id, name: item.name });
            }
          });
        }
        
        // Pack Type (many-to-many) - API returns as 'packType' (singular)
        if (product.packType && Array.isArray(product.packType)) {
          product.packType.forEach((item) => {
            if (!options.packType.find(o => o.id === item.id)) {
              options.packType.push({ id: item.id, name: item.name });
            }
          });
        }
      });

      return options;
    } catch (error) {
      console.error('Failed to get filter options:', error);
      return {
        category: [],
        productName: [],
        finish: [],
        color: [],
        suitableFor: [],
        material: [],
        collection: [],
        packType: [],
      };
    }
  },

  /**
   * Get a single product by ID
   */
  getProduct: async (productId) => {
    try {
      const response = await apiClient.get(`${API_BASE_URL}/products/${productId}`);
      return response.data.data || response.data;
    } catch (error) {
      console.error('Failed to fetch product:', error);
      return null;
    }
  },

  /**
   * Get a product by UID
   */
  getProductByUID: async (uid) => {
    try {
      // Fetch all products and find by UID
      const response = await apiClient.get(`${API_BASE_URL}/products`);
      const products = response.data.data || response.data || [];
      const product = Array.isArray(products) ? products.find(p => p.uid === uid) : null;
      return product || null;
    } catch (error) {
      console.error('Failed to fetch product by UID:', error);
      return null;
    }
  },

  /**
   * Create a new product
   */
  createProduct: async (productData) => {
    try {
      const formData = new FormData();

      // Add text fields
      formData.append('itemNo', productData.itemNo);
      formData.append('category', productData.category);
      formData.append('productName', productData.productName);
      formData.append('hsCode', productData.hsCode || '');
      formData.append('suitableFor', productData.suitableFor || '');
      formData.append('lengthDimension', productData.lengthDimension);
      formData.append('breadthDimension', productData.breadthDimension);
      formData.append('heightDimension', productData.heightDimension);
      formData.append('dimensionUnit', productData.dimensionUnit);
      formData.append('netWeight', productData.netWeight);
      formData.append('weightUnit', productData.weightUnit);
      formData.append('moq', productData.moq);
      formData.append('cost', productData.cost);
      formData.append('remark', productData.remark || '');
      formData.append('mainImageIndex', productData.mainImageIndex || 0);

      // Add JSON arrays
      if (productData.color && productData.color.length > 0) {
        formData.append('color', JSON.stringify(productData.color));
      }
      if (productData.finish && productData.finish.length > 0) {
        formData.append('finish', JSON.stringify(productData.finish));
      }
      if (productData.material && productData.material.length > 0) {
        formData.append('material', JSON.stringify(productData.material));
      }
      if (productData.collection && productData.collection.length > 0) {
        formData.append('collection', JSON.stringify(productData.collection));
      }
      if (productData.packType && productData.packType.length > 0) {
        formData.append('packType', JSON.stringify(productData.packType));
      }
      if (productData.innerPacks && productData.innerPacks.length > 0) {
        formData.append('innerPacks', JSON.stringify(productData.innerPacks));
      }
      if (productData.masterPacks && productData.masterPacks.length > 0) {
        formData.append('masterPacks', JSON.stringify(productData.masterPacks));
      }

      // Add images - only File objects
      if (productData.images && Array.isArray(productData.images)) {
        productData.images.forEach((image) => {
          if (image instanceof File) {
            formData.append('images[]', image);
          }
        });
      }

      const response = await apiClient.post(`${API_BASE_URL}/products`, formData);
      return response.data.data || response.data;
    } catch (error) {
      console.error('Failed to create product:', error);
      throw error;
    }
  },

  /**
   * Update a product
   */
  updateProduct: async (productId, productData) => {
    try {
      const formData = new FormData();

      // Add text fields
      formData.append('itemNo', productData.itemNo);
      formData.append('category', productData.category);
      formData.append('productName', productData.productName);
      formData.append('hsCode', productData.hsCode || '');
      formData.append('suitableFor', productData.suitableFor || '');
      formData.append('lengthDimension', productData.lengthDimension);
      formData.append('breadthDimension', productData.breadthDimension);
      formData.append('heightDimension', productData.heightDimension);
      formData.append('dimensionUnit', productData.dimensionUnit);
      formData.append('netWeight', productData.netWeight);
      formData.append('weightUnit', productData.weightUnit);
      formData.append('moq', productData.moq);
      formData.append('cost', productData.cost);
      formData.append('remark', productData.remark || '');
      formData.append('mainImageIndex', productData.mainImageIndex || 0);

      // Add JSON arrays
      if (productData.color && productData.color.length > 0) {
        formData.append('color', JSON.stringify(productData.color));
      }
      if (productData.finish && productData.finish.length > 0) {
        formData.append('finish', JSON.stringify(productData.finish));
      }
      if (productData.material && productData.material.length > 0) {
        formData.append('material', JSON.stringify(productData.material));
      }
      if (productData.collection && productData.collection.length > 0) {
        formData.append('collection', JSON.stringify(productData.collection));
      }
      if (productData.packType && productData.packType.length > 0) {
        formData.append('packType', JSON.stringify(productData.packType));
      }
      if (productData.innerPacks && productData.innerPacks.length > 0) {
        formData.append('innerPacks', JSON.stringify(productData.innerPacks));
      }
      if (productData.masterPacks && productData.masterPacks.length > 0) {
        formData.append('masterPacks', JSON.stringify(productData.masterPacks));
      }

      // Add images only if provided - only File objects
      if (productData.images && Array.isArray(productData.images)) {
        productData.images.forEach((image) => {
          if (image instanceof File) {
            formData.append('images[]', image);
          }
        });
      }

      const response = await apiClient.patch(`${API_BASE_URL}/products/${productId}`, formData);
      return response.data.data || response.data;
    } catch (error) {
      console.error('Failed to update product:', error);
      throw error;
    }
  },

  /**
   * Delete a product
   */
  deleteProduct: async (productId) => {
    try {
      const response = await apiClient.delete(`${API_BASE_URL}/products/${productId}`);
      return response.data;
    } catch (error) {
      console.error('Failed to delete product:', error);
      throw error;
    }
  },
};

export default productService;
